import { useEffect, useMemo, useRef, useState } from 'react';
import type { LegacyCourierTracking, LegacyPoint } from '@/entities/legacy-order';
import { fetchLegacyRoute, type LegacyRoutePath } from '@/entities/legacy-order-draft';
import {
  legacyCourierRoutePoints,
  legacyIsOffRoute,
  legacyNextPoint,
  type LegacyRoutePoint,
} from '../lib/order-detail';

/**
 * The drawer's road-snapped route, held ABOVE the map component — the source
 * kept `routeResponse` in `OrderSingleBox` for the same reason: the map
 * remounts when it toggles fullscreen (and when its container resizes), and
 * remount-local state would refetch the route on every toggle.
 *
 * What gets routed (the source's `customPoints`): with a courier fix the line
 * runs courier → dispatcher target → next stop (or courier → next stop);
 * without one it runs through the plain stop list. See
 * `legacyCourierRoutePoints` for the transcription.
 *
 * Refetch triggers, both the source's:
 * - the route's STRUCTURE changes — a courier appears/disappears, the target
 *   or next stop moves, a stop is edited. The courier's own live position is
 *   deliberately NOT part of that key, or the 5s tracking poll would refetch
 *   the route on every tick.
 * - the courier drifts ≥ 100 m off the drawn polyline
 *   (`OrderSingleBox.tsx:288-300`), measured with the source's own
 *   nearest-point + haversine pair, axis quirk included.
 */
export function useLegacyOrderRoute({
  points,
  tracking,
}: {
  points: LegacyRoutePoint[];
  tracking: LegacyCourierTracking | null;
}): {
  /** The polyline to draw, or null while none applies. */
  route: LegacyRoutePath | null;
  /** The courier-first point list when one is being routed, else null. */
  courierPath: LegacyPoint[] | null;
} {
  const [route, setRoute] = useState<LegacyRoutePath | null>(null);

  const placedLocations = useMemo(
    () =>
      points
        .map((p) => p.location)
        .filter((l): l is LegacyPoint => Boolean(l?.lat && l?.long)),
    [points],
  );

  const nextPoint = useMemo(() => legacyNextPoint(points), [points]);

  const courierLocation = tracking?.location ?? null;
  const target = tracking?.target_destination ?? null;

  const courierPath = useMemo(
    () => legacyCourierRoutePoints({ location: courierLocation, target, nextPoint }),
    [courierLocation, target, nextPoint],
  );

  const requestPoints =
    courierPath ?? (placedLocations.length >= 2 ? placedLocations : null);

  /*
   * The structure key: everything about the requested route EXCEPT the
   * courier's live position (the first entry of a courier path). The prefix
   * distinguishes a courier route from a stop-list route whose coordinates
   * happen to match.
   */
  const structureKey = `${courierPath ? 'courier' : 'stops'}:${(courierPath
    ? courierPath.slice(1)
    : placedLocations
  )
    .map((p) => `${p.long},${p.lat}`)
    .join('|')}`;

  // Monotonic sequence so a slow response can never overwrite a newer one.
  // (Written only inside effects/callbacks — never read during render.)
  const seqRef = useRef(0);

  // Recreated every render — deliberately NOT memoized: each effect run
  // closes over the render-current `requestPoints`, which is exactly what a
  // refetch should route through.
  const requestRoute = () => {
    if (!requestPoints || requestPoints.length < 2) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    void fetchLegacyRoute(requestPoints).then((path) => {
      if (seq === seqRef.current) setRoute(path);
    });
  };

  useEffect(() => {
    requestRoute();
    // `structureKey` IS the identity of everything the fetch reads — the
    // courier's live position is deliberately excluded (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureKey]);

  useEffect(() => {
    if (!courierLocation || !route?.length) return;
    if (legacyIsOffRoute(courierLocation, route)) requestRoute();
    // Only an actual MOVE should re-check drift — not the route refresh the
    // drift itself just triggered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierLocation?.lat, courierLocation?.long]);

  return { route: requestPoints ? route : null, courierPath };
}
